
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { networkErrorHandler, getErrorMessage, getFallbackData } from '@/utils/networkErrorHandler';

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: string;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  display_type: string;
  post_frequency: number | null;
  created_at: string;
}

export const useAnnouncements = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchAnnouncements = async (isRetry = false) => {
    try {
      if (!isRetry) {
        setLoading(true);
        setError(null);
      }

      const result = await networkErrorHandler.withRetry(async () => {
        const { data, error } = await supabase
          .from('announcements')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
      }, {
        maxRetries: 2,
        delayMs: 1000
      });

      // Filter announcements based on date constraints
      const now = new Date();
      const activeAnnouncements = (result || []).filter(announcement => {
        // Check start date
        if (announcement.start_date) {
          const startDate = new Date(announcement.start_date);
          if (now < startDate) return false;
        }
        
        // Check end date
        if (announcement.end_date) {
          const endDate = new Date(announcement.end_date);
          if (now > endDate) return false;
        }
        
        return true;
      });

      setAnnouncements(activeAnnouncements);
      setRetryCount(0); // Reset retry count on success
      
    } catch (err: any) {
      console.error('Error fetching announcements:', err);
      
      const userMessage = getErrorMessage(err);
      setError(userMessage);
      
      // Use fallback data if available
      if (networkErrorHandler.isOffline()) {
        const fallbackData = getFallbackData('announcements');
        setAnnouncements(fallbackData);
      } else {
        setAnnouncements([]);
      }
      
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const retry = () => {
    fetchAnnouncements(true);
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  return {
    announcements,
    loading,
    error,
    retryCount,
    refetch: fetchAnnouncements,
    retry,
    isOffline: networkErrorHandler.isOffline()
  };
};
